import React, { useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ContentContainer from "../components/ContentContainer";
import PageHeader from "../components/PageHeader";
import VideoPlayer from "../components/VideoPlayer";
import VideoSticker from "../components/VideoSticker";
import "./Why.css";

const SOLUTION_CARD_COUNT = 8; // 8 benefit cards

function Why() {
  const { t } = useTranslation();
  const carouselRef = useRef(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const scrollToIndex = useCallback((index) => {
    const el = carouselRef.current;
    if (!el) return;
    const cardWidth = el.querySelector(".why-solution-card")?.offsetWidth ?? 320;
    const gap = 16;
    el.scrollTo({ left: index * (cardWidth + gap), behavior: "smooth" });
    setCarouselIndex(index);
  }, []);

  const handleCarouselScroll = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    const cardWidth = el.querySelector(".why-solution-card")?.offsetWidth ?? 320;
    const gap = 16;
    const index = Math.round(el.scrollLeft / (cardWidth + gap));
    setCarouselIndex(Math.min(index, SOLUTION_CARD_COUNT - 1));
  }, []);

  return (
    <div className="why">
      <PageHeader title={t("why.title")} />

      <ContentContainer>
        <section className="why-hero">
          <h2 className="why-hero-title">{t("why.heroTitle")}</h2>
          <p className="why-hero-lead">
            <strong>{t("why.heroDesc")}</strong>
          </p>
        </section>
      </ContentContainer>

      <ContentContainer>
        <div className="why-content">

        <section className="problem-section">
          <h2>{t("why.problemTitle")}</h2>
          
          <div className="problem-card">
            <h3>{t("why.problemSatochipTitle")}</h3>
            <p>
              {t("why.problemSatochipDesc")}
            </p>
          </div>

          <div className="problem-card">
            <h3>{t("why.problemSeedSignerTitle")}</h3>
            <p>
              {t("why.problemSeedSignerDesc")}
            </p>
          </div>
        </section>

        <section className="solution-section">
          <h2>{t("why.solutionTitle")}</h2>
          <p className="why-solution-intro">{t("why.solutionDesc")}</p>

          <div className="why-solution-carousel-wrap">
            <div
              className="why-solution-carousel"
              ref={carouselRef}
              onScroll={handleCarouselScroll}
              role="region"
              aria-label={t("why.benefitsTitle")}
            >
              <div className="why-solution-carousel-inner">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="why-solution-card">
                    <h3>{t(`why.benefit${i}Title`)}</h3>
                    <p>{t(`why.benefit${i}Desc`)}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="why-solution-carousel-nav">
              <button
                type="button"
                className="carousel-btn carousel-prev"
                onClick={() => scrollToIndex(Math.max(0, carouselIndex - 1))}
                disabled={carouselIndex === 0}
                aria-label="Previous"
              >
                ←
              </button>
              <div className="carousel-dots">
                {Array.from({ length: SOLUTION_CARD_COUNT }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`carousel-dot ${i === carouselIndex ? "active" : ""}`}
                    onClick={() => scrollToIndex(i)}
                    aria-label={`Go to card ${i + 1}`}
                    aria-current={i === carouselIndex ? "true" : undefined}
                  />
                ))}
              </div>
              <button
                type="button"
                className="carousel-btn carousel-next"
                onClick={() => scrollToIndex(Math.min(SOLUTION_CARD_COUNT - 1, carouselIndex + 1))}
                disabled={carouselIndex === SOLUTION_CARD_COUNT - 1}
                aria-label="Next"
              >
                →
              </button>
            </div>
          </div>
        </section>

        <section className="power-section">
          <h2>{t("why.powerTitle")}</h2>
          <div className="power-section-layout">
            <div className="power-section-text">
              <div className="power-highlight">
                <p>
                  <strong>{t("why.powerDesc1")}</strong>
                </p>
                <p>
                  {t("why.powerDesc2")}
                </p>
              </div>
              <div className="power-benefits">
                <h3>{t("why.powerBenefitsTitle")}</h3>
                <ul>
                  <li>
                    <strong>{t("why.powerBenefit1Title")}</strong> {t("why.powerBenefit1Desc")}
                  </li>
                  <li>
                    <strong>{t("why.powerBenefit2Title")}</strong> {t("why.powerBenefit2Desc")}
                  </li>
                  <li>
                    <strong>{t("why.powerBenefit3Title")}</strong> {t("why.powerBenefit3Desc")}
                  </li>
                  <li>
                    <strong>{t("why.powerBenefit4Title")}</strong> {t("why.powerBenefit4Desc")}
                  </li>
                </ul>
              </div>
            </div>
            <div className="power-section-video">
              <div className="video-section">
                <VideoPlayer
                  url={`${process.env.PUBLIC_URL}/assets/videos/video_01.mp4`}
                  maxWidth="400px"
                  maxHeight="600px"
                  aspectRatio="9 / 16"
                >
                  <VideoSticker
                    imageSrc={`${process.env.PUBLIC_URL}/assets/images/image_nwbs.webp`}
                    imageAlt="No Waste BTC Signs"
                    linkUrl="https://x.com/NoWasteBTCsigns"
                    linkTitle="No Waste BTC Signs - Click to visit Twitter"
                  />
                </VideoPlayer>
              </div>
            </div>
          </div>
        </section>

        <section className="closing-section">
          <h2>{t("why.closingTitle")}</h2>
          <p className="closing">
            {t("why.closingDesc")}
          </p>
        </section>
      </div>
      </ContentContainer>
    </div>
  );
}

export default Why;

